import {
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'

import { useGetPrivacyEventSampleQuery } from '@/apis/opsApi'
import type { V2RangeArgs } from '@/apis/v2Mappers'
import { ReportStatus, SectionCard, fmtDateTime } from '@/modules/reports/common'

/**
 * Per-event opt-out evidence: the most recent events (≤ 50) that carried GPC / DNT / denied
 * consent or had a privacy action applied, with no visitor, session or user ids.
 */
export function PrivacyEventsCard({ args }: { args: V2RangeArgs }) {
    const { data, isLoading, error } = useGetPrivacyEventSampleQuery(args)
    return (
        <SectionCard title="Per-event evidence (most recent)">
            <ReportStatus
                isLoading={isLoading}
                error={error}
                isEmpty={!!data && data.events.length === 0}
                emptyTitle="No opt-out events in this range"
                emptyDescription="Events with GPC, DNT, denied consent, scrubbed fields or suppressed forwarding are listed here."
            >
                {data && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Each row is one event and what YOM did with it. No identifiers or event
                            parameters are shown.
                            {data.sampled ? ' Taken from the sampled event store.' : ''} The
                            printable evidence includes this list as an appendix.
                        </Typography>
                        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Time</TableCell>
                                        <TableCell>Event</TableCell>
                                        <TableCell>Path</TableCell>
                                        <TableCell>Signals and actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.events.map((e, i) => (
                                        <TableRow key={`${e.at}-${i}`}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {fmtDateTime(e.at)}
                                            </TableCell>
                                            <TableCell>
                                                {e.event}
                                                {e.dropped > 0 ? ` (${e.dropped})` : ''}
                                            </TableCell>
                                            <TableCell>{e.path}</TableCell>
                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    {e.reasons.map(r => (
                                                        <Chip
                                                            key={r}
                                                            size="small"
                                                            label={r}
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                )}
            </ReportStatus>
        </SectionCard>
    )
}
